import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Home, Scale, ClipboardCheck, CheckCircle2, ArrowRight, Info, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ComplianceStrip from "../components/ComplianceStrip";
import Seo from "../components/Seo";
import { api, track } from "../lib/api";

/* Concise, reusable promotional small print for anywhere the £100 offer is
   shown. Significant conditions are visible — never hidden behind a link. */
export const CASHBACK_SMALLPRINT =
  "£100 cashback when you use all three qualifying MortgagePath routes — mortgage, conveyancing and survey — and your property purchase completes. England & Wales. 18+. One payment per property purchase. Claim within 90 days of completion. T&Cs apply.";

const HERO_SMALLPRINT =
  "Available on qualifying residential purchases in England and Wales. You must use all three eligible MortgagePath routes — mortgage, conveyancing and survey — and your purchase must complete. One £100 cashback payment per property purchase. Claim within 90 days of completion. Full terms apply.";

const STEPS = [
  {
    icon: Home,
    title: "1. Mortgage",
    body: "Access fee-free mortgage advice through MortgagePath's selected regulated mortgage broker route. The mortgage used to purchase the property must be arranged through that route and successfully complete.",
  },
  {
    icon: Scale,
    title: "2. Conveyancing",
    body: "Request and instruct your purchase conveyancing through MortgagePath's selected conveyancing route. The conveyancing matter must proceed through that partner to completion.",
  },
  {
    icon: ClipboardCheck,
    title: "3. Property survey",
    body: "Arrange and pay for a qualifying property survey through MortgagePath's selected survey route. The survey must be successfully carried out.",
  },
];

const TERMS = [
  { h: "1. Promoter", p: ["The promoter is Home Finance Group Ltd, trading as MortgagePath, a company registered in England and Wales under company number 17288752.", "Registered office: 128 City Road, London, EC1V 2NX, United Kingdom."] },
  { h: "2. The offer", p: ["Eligible customers can receive one £100 cashback payment per qualifying completed property purchase where all requirements in these terms are satisfied.", "This is a cashback promotion and is not a prize draw or competition."] },
  { h: "3. Promotion period", p: ["The offer applies to qualifying services initiated through MortgagePath on or after 23 September 2026.", "The offer is currently ongoing with no published closing date.", "If MortgagePath decides to close the promotion to new participants, notice will be published on this page. Closing the promotion will not remove the ability of customers who had already validly begun all three qualifying MortgagePath partner journeys before the closure date to make an eligible claim, provided all other conditions are met."] },
  { h: "4. Who can claim", p: ["The claimant must: be aged 18 or over; be a purchaser or joint purchaser of the property; be completing a qualifying residential property purchase in England or Wales; have used all three qualifying MortgagePath partner routes; and satisfy the conditions for each qualifying service below.", "The promotion does not currently apply to: remortgages where no property purchase takes place; standalone conveyancing; standalone surveys; mortgage-only referrals; commercial property transactions; or transactions where fewer than all three qualifying services have been used.", "One cashback payment is available per property purchase, not per purchaser. Joint purchasers cannot make separate claims for the same transaction."] },
  { h: "5. Qualifying mortgage service", p: ["The mortgage used to purchase the property must have been arranged through MortgagePath's selected regulated mortgage broker route.", "The customer must access the broker through the relevant MortgagePath link or tracked journey.", "The mortgage must proceed to completion in connection with the property included in the cashback claim.", "Simply visiting the broker's website does not qualify."] },
  { h: "6. Qualifying conveyancing service", p: ["The customer must request and instruct their purchase conveyancing through MortgagePath's selected conveyancing referral route.", "The conveyancing matter must proceed through that provider to successful legal completion."] },
  { h: "7. Qualifying property survey", p: ["The customer must book and pay for an eligible property survey through MortgagePath's selected survey route and the survey must be completed.", "A lender's mortgage valuation alone is not a qualifying property survey for this promotion.", "Availability of particular survey products depends on the property and the survey provider's service criteria."] },
  { h: "8. MortgagePath tracking", p: ["Customers should use the relevant MortgagePath links and forms when accessing each service.", "Going directly to a provider without using the MortgagePath route may mean MortgagePath cannot verify the referral.", "MortgagePath may consider reasonable supporting evidence where a customer believes a qualifying referral was made through MortgagePath but a genuine tracking issue has occurred."] },
  { h: "9. When to claim", p: ["A cashback claim can only be submitted after the legal completion of the property purchase.", "Claims must be submitted within 90 days of the purchase completion date.", "Customers must provide sufficient information for MortgagePath to verify all three qualifying services."] },
  { h: "10. Verification", p: ["MortgagePath will check claims against its own records and, where necessary, the records of its selected mortgage broker, conveyancing and survey partners.", "By submitting a claim, the customer asks MortgagePath to carry out this verification and acknowledges that the minimum information necessary may be shared with those partners for that purpose.", "Submitting a claim does not itself guarantee cashback.", "If information is missing or cannot initially be matched, MortgagePath may contact the customer for reasonable further information or evidence before deciding the claim."] },
  { h: "11. Uncompleted or cancelled transactions", p: ["Cashback is not payable where the property purchase does not complete.", "Cashback is also not payable where a qualifying service is cancelled, refunded or does not proceed far enough to satisfy the qualifying requirements set out above."] },
  { h: "12. Payment", p: ["Approved claims receive £100.", "Payment will normally be made by UK bank transfer.", "MortgagePath will request any payment information separately after the cashback claim has been approved. Customers should not enter bank information into the main cashback claim form.", "MortgagePath will normally make payment within 30 days of successfully verifying an eligible claim.", "The bank account used to receive cashback should normally be held in the claimant's name or jointly in their name."] },
  { h: "13. Duplicate or inaccurate claims", p: ["Only one cashback payment is available for each qualifying property purchase.", "MortgagePath may reject duplicate, fraudulent, deliberately misleading or materially inaccurate claims.", "MortgagePath may ask for reasonable evidence where necessary to prevent misuse of the promotion."] },
  { h: "14. Partner relationships", p: ["MortgagePath provides educational mortgage information and is not a mortgage broker, lender or regulated mortgage adviser.", "Mortgage advice is provided separately by the regulated mortgage broker. Conveyancing and survey services are provided by the relevant independent providers under their own terms.", "MortgagePath may receive commission or referral fees where customers use partner services. These commercial arrangements are disclosed on the MortgagePath website.", "The £100 cashback is provided by MortgagePath under these promotional terms."] },
  { h: "15. Personal information", p: ["MortgagePath will use personal information submitted through a cashback claim to: administer the cashback promotion; verify eligibility; prevent duplicate or fraudulent claims; contact the claimant about their claim; and arrange and record an approved cashback payment.", "The minimum information necessary may be shared with relevant MortgagePath partners where required to verify the claim.", "Further information is available in the MortgagePath Privacy Policy."] },
  { h: "16. Changes to the promotion", p: ["MortgagePath may close the promotion to new participants or make changes where reasonably necessary.", "Changes will not unfairly remove cashback eligibility from customers who have already satisfied the promotion requirements.", "The latest terms will be published on this page."] },
  { h: "17. Governing law", p: ["These terms are governed by the laws of England and Wales.", "Nothing in these terms affects a consumer's statutory rights."] },
];

const TODAY = new Date().toISOString().split("T")[0];

const initialForm = {
  full_name: "",
  email: "",
  property_postcode: "",
  completion_date: "",
  mortgage_reference: "",
  conveyancing_reference: "",
  survey_reference: "",
  confirm_purchaser: false,
  confirm_services: false,
  agree_terms: false,
  agree_data_sharing: false,
  company: "", // honeypot
};

const ErrorText = ({ msg }) => (msg ? <span className="block mt-1 text-xs text-[#B91C1C]">{msg}</span> : null);

const validate = (f) => {
  const e = {};
  if (!String(f.full_name).trim()) e.full_name = "Please enter your full name.";
  if (!String(f.email).trim()) e.email = "Please enter your email address.";
  else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "Please enter a valid email address.";
  if (!String(f.property_postcode).trim()) e.property_postcode = "Please enter the property postcode.";
  if (!f.completion_date) e.completion_date = "Please enter your completion date.";
  else if (f.completion_date > TODAY) e.completion_date = "Completion date cannot be in the future.";
  if (!String(f.mortgage_reference).trim()) e.mortgage_reference = "Please enter the mortgage reference.";
  if (!String(f.conveyancing_reference).trim()) e.conveyancing_reference = "Please enter the conveyancing reference.";
  if (!String(f.survey_reference).trim()) e.survey_reference = "Please enter the survey reference.";
  if (!f.confirm_purchaser) e.confirm_purchaser = "Please confirm this to continue.";
  if (!f.confirm_services) e.confirm_services = "Please confirm this to continue.";
  if (!f.agree_terms) e.agree_terms = "Please agree to the Terms and Conditions.";
  if (!f.agree_data_sharing) e.agree_data_sharing = "Please confirm this to continue.";
  return e;
};

const Check = ({ name, checked, onChange, testid, error, children }) => (
  <label className="flex gap-3 items-start cursor-pointer">
    <input type="checkbox" className="mt-1 w-4 h-4 accent-[#0F766E] shrink-0" checked={checked} onChange={(e) => onChange(name, e.target.checked)} data-testid={testid} />
    <span className="text-sm text-[#475569] leading-relaxed">
      {children}
      <ErrorText msg={error} />
    </span>
  </label>
);

const SuccessView = ({ reference }) => (
  <div className="mt-8 mp-card p-8 sm:p-10 text-center" data-testid="cashback-success">
    <div className="mx-auto w-14 h-14 rounded-full bg-[#10B981]/10 grid place-items-center">
      <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
    </div>
    <h2 className="font-sora text-3xl font-bold text-[#0B1220] mt-4 tracking-tight">Claim received</h2>
    <p className="mt-3 text-[#475569] leading-relaxed max-w-xl mx-auto">
      Thanks — we've received your £100 Homebuyer Cashback claim.
    </p>
    <div className="mt-6 inline-flex flex-col items-center gap-1 rounded-xl bg-[#F7FAF9] border border-[#DCE7E3] px-6 py-4">
      <span className="text-xs uppercase tracking-widest text-[#0F766E]">Your claim reference</span>
      <span className="font-sora text-lg font-semibold text-[#0B1220]" data-testid="cashback-reference">{reference}</span>
      <span className="text-xs text-[#475569]">Keep this in case you need to contact us.</span>
    </div>
    <p className="mt-6 text-sm text-[#475569] leading-relaxed max-w-xl mx-auto">
      We'll check the details against our partner records. If your claim qualifies, we'll contact you using the email
      address you provided. We will not ask you to submit bank details through the cashback claim form.
    </p>
    <div className="mt-7">
      <Link to="/" className="mp-btn-primary text-sm" data-testid="cashback-home">Back to MortgagePath <ArrowRight className="w-4 h-4" /></Link>
    </div>
  </div>
);

/* Neutral placeholder shown at /cashback while the promotion is disabled for
   compliance review. No promotional wording; noindex so search engines drop
   the page while it is unavailable. */
export const CashbackUnavailable = () => (
  <div className="mp-container py-20 max-w-2xl text-center" data-testid="cashback-unavailable">
    <Seo
      title="Promotion unavailable | MortgagePath"
      description="This promotion is currently unavailable."
      path="/cashback"
      noindex
    />
    <h1 className="font-sora text-3xl sm:text-4xl font-bold tracking-tight text-[#0B1220]">
      This promotion is currently unavailable
    </h1>
    <p className="mt-4 text-[#475569] leading-relaxed">
      This promotion is currently unavailable. You can carry on exploring MortgagePath's free educational tools and guides.
    </p>
    <div className="mt-8">
      <Link to="/" className="mp-btn-primary" data-testid="cashback-unavailable-home">
        Back to MortgagePath <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </div>
);

const Cashback = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const formRef = useRef(null);

  useEffect(() => { track("cashback_view", { source: "cashback_page" }); }, []);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));
  };

  const scrollToClaim = () => {
    track("cashback_claim_start", { source: "hero_cta" });
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete all required fields and confirmations.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/cashback-claim", form);
      setReference(data.reference);
      track("cashback_claim_submitted", { claim_reference: data.reference, submission_status: "received" });
      toast.success("Your cashback claim has been received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries and try again.");
      track("cashback_claim_error", { status: status || 0 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mp-container py-14 max-w-4xl">
      <Seo
        title="£100 Homebuyer Cashback | MortgagePath"
        description="Use MortgagePath for your mortgage, conveyancing and qualifying property survey and claim £100 cashback when your home purchase completes. Terms apply."
        path="/cashback"
      />

      {/* Hero */}
      <div className="text-xs uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
        <Gift className="w-3.5 h-3.5" /> A MortgagePath benefit
      </div>
      <h1 className="font-sora mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0B1220]">
        £100 Homebuyer Cashback
      </h1>
      <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl text-base">
        Buying a home? Use all three qualifying MortgagePath partner routes for your mortgage, conveyancing and property
        survey, and you can claim £100 cashback when your purchase completes.
      </p>
      <p className="mt-2 text-[#475569] leading-relaxed max-w-2xl text-base">
        One less cost to worry about when you're moving home.
      </p>
      <div className="mt-6">
        <button type="button" onClick={scrollToClaim} className="mp-btn-primary" data-testid="cashback-hero-cta">
          Claim my £100 cashback <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      <p className="mt-5 text-xs text-[#475569] leading-relaxed max-w-2xl" data-testid="cashback-hero-smallprint">
        {HERO_SMALLPRINT}
      </p>

      {/* How it works */}
      <section className="mt-14">
        <h2 className="font-sora text-2xl sm:text-3xl font-bold text-[#0B1220] tracking-tight">Three services. One £100 cashback.</h2>
        <div className="mt-7 grid gap-5 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.title} className="mp-card p-6" data-testid={`cashback-step-${s.title.charAt(0)}`}>
              <span className="w-11 h-11 rounded-xl bg-[#0F766E]/10 grid place-items-center text-[#0F766E]">
                <s.icon className="w-5 h-5" />
              </span>
              <h3 className="font-sora text-lg font-semibold text-[#0B1220] mt-4">{s.title}</h3>
              <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl bg-[#F7FAF9] border border-[#DCE7E3] p-6">
          <h3 className="font-sora text-lg font-semibold text-[#0B1220]">Once your purchase completes</h3>
          <p className="mt-2 text-sm text-[#475569] leading-relaxed">
            Come back to MortgagePath, submit your three references below and we'll check them against our partner records.
            Once your claim has been verified, we'll arrange your £100 cashback.
          </p>
        </div>

        {/* Tracking callout */}
        <div className="mt-6 rounded-2xl border p-5 flex gap-4" style={{ background: "#EFF6FF", borderColor: "#93C5FD" }} data-testid="cashback-tracking-callout">
          <span className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
            <Info className="w-5 h-5" />
          </span>
          <div>
            <h3 className="font-sora text-base font-semibold text-[#0B1220]">Make sure your referrals can be tracked</h3>
            <p className="mt-1.5 text-sm text-[#334155] leading-relaxed">
              To qualify, all three services must be accessed through the relevant MortgagePath link, quote form or referral
              journey. If you go directly to a provider rather than using MortgagePath, we may not be able to verify the
              referral and it may not qualify for cashback. If there is a genuine tracking issue, MortgagePath may consider
              reasonable supporting evidence when reviewing the claim.
            </p>
          </div>
        </div>
      </section>

      {/* Claim form */}
      <section id="claim" ref={formRef} className="mt-14 scroll-mt-24">
        <h2 className="font-sora text-2xl sm:text-3xl font-bold text-[#0B1220] tracking-tight">Claim your £100 cashback</h2>
        <p className="mt-3 text-[#475569] leading-relaxed max-w-2xl">
          Already completed your purchase? Submit your details below and we'll verify your mortgage, conveyancing and
          survey referrals.
        </p>

        <div className="mt-5 rounded-xl border p-4 flex gap-3 items-start" style={{ background: "#ECFDF5", borderColor: "#6EE7B7" }} data-testid="cashback-security-note">
          <ShieldCheck className="w-5 h-5 text-[#0F766E] mt-0.5 shrink-0" />
          <p className="text-sm text-[#334155] leading-relaxed">
            For your security, we don't ask for bank details when you submit your claim. If your claim is approved, we'll
            contact you separately to arrange your £100 payment securely.
          </p>
        </div>

        {reference ? (
          <SuccessView reference={reference} />
        ) : (
          <form onSubmit={onSubmit} className="mt-6 space-y-5" data-testid="cashback-form" noValidate>
            {/* Honeypot — visually hidden, off-screen; bots fill it, humans don't. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
              <label>Company
                <input type="text" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="cashback-company" />
              </label>
            </div>

            <div className="mp-card p-6 sm:p-7">
              <h3 className="font-sora text-lg font-semibold text-[#0B1220]">Your details</h3>
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <label className="block sm:col-span-2">
                  <span className="mp-label">Full name <span className="text-[#0F766E]">*</span></span>
                  <input type="text" className="mp-input" value={form.full_name} placeholder="e.g. Alex Taylor" onChange={(e) => set("full_name", e.target.value)} data-testid="cashback-full_name" />
                  <span className="block mt-1 text-xs text-[#94A3B8]">You should be a purchaser or joint purchaser of the property.</span>
                  <ErrorText msg={errors.full_name} />
                </label>

                <label className="block">
                  <span className="mp-label">Email address <span className="text-[#0F766E]">*</span></span>
                  <input type="email" className="mp-input" value={form.email} placeholder="you@example.com" onChange={(e) => set("email", e.target.value)} data-testid="cashback-email" />
                  <span className="block mt-1 text-xs text-[#94A3B8]">Ideally use the same email address you used with the partner services.</span>
                  <ErrorText msg={errors.email} />
                </label>

                <label className="block">
                  <span className="mp-label">Property postcode <span className="text-[#0F766E]">*</span></span>
                  <input type="text" className="mp-input" value={form.property_postcode} placeholder="e.g. EC1V 2NX" onChange={(e) => set("property_postcode", e.target.value.toUpperCase())} data-testid="cashback-postcode" maxLength={12} />
                  <ErrorText msg={errors.property_postcode} />
                </label>

                <label className="block sm:col-span-2">
                  <span className="mp-label">Purchase completion date <span className="text-[#0F766E]">*</span></span>
                  <input type="date" className="mp-input" value={form.completion_date} max={TODAY} onChange={(e) => set("completion_date", e.target.value)} data-testid="cashback-completion_date" />
                  <ErrorText msg={errors.completion_date} />
                </label>
              </div>
            </div>

            <div className="mp-card p-6 sm:p-7">
              <h3 className="font-sora text-lg font-semibold text-[#0B1220]">Your three MortgagePath references</h3>
              <div className="grid gap-5 mt-5">
                <label className="block">
                  <span className="mp-label">Mortgage broker reference / case number <span className="text-[#0F766E]">*</span></span>
                  <input type="text" className="mp-input" value={form.mortgage_reference} onChange={(e) => set("mortgage_reference", e.target.value)} data-testid="cashback-mortgage_reference" />
                  <span className="block mt-1 text-xs text-[#94A3B8]">Enter the case or customer reference provided by the mortgage broker.</span>
                  <ErrorText msg={errors.mortgage_reference} />
                </label>
                <label className="block">
                  <span className="mp-label">Conveyancing reference / matter number <span className="text-[#0F766E]">*</span></span>
                  <input type="text" className="mp-input" value={form.conveyancing_reference} onChange={(e) => set("conveyancing_reference", e.target.value)} data-testid="cashback-conveyancing_reference" />
                  <span className="block mt-1 text-xs text-[#94A3B8]">Enter the reference for your purchase conveyancing.</span>
                  <ErrorText msg={errors.conveyancing_reference} />
                </label>
                <label className="block">
                  <span className="mp-label">Property survey reference <span className="text-[#0F766E]">*</span></span>
                  <input type="text" className="mp-input" value={form.survey_reference} onChange={(e) => set("survey_reference", e.target.value)} data-testid="cashback-survey_reference" />
                  <span className="block mt-1 text-xs text-[#94A3B8]">Enter the booking, order or survey reference.</span>
                  <ErrorText msg={errors.survey_reference} />
                </label>
              </div>
            </div>

            <div className="mp-card p-6 sm:p-7 space-y-4">
              <h3 className="font-sora text-lg font-semibold text-[#0B1220]">Confirmations</h3>
              <Check name="confirm_purchaser" checked={form.confirm_purchaser} onChange={set} testid="cashback-confirm_purchaser" error={errors.confirm_purchaser}>
                I confirm that I am a purchaser or joint purchaser of this property and that the information in this claim is accurate.
              </Check>
              <Check name="confirm_services" checked={form.confirm_services} onChange={set} testid="cashback-confirm_services" error={errors.confirm_services}>
                I confirm that I accessed all three qualifying services through MortgagePath and that the property purchase has completed.
              </Check>
              <Check name="agree_terms" checked={form.agree_terms} onChange={set} testid="cashback-agree_terms" error={errors.agree_terms}>
                I agree to the <a href="#terms" className="text-[#0F766E] underline">£100 Homebuyer Cashback Terms and Conditions</a>.
              </Check>
              <Check name="agree_data_sharing" checked={form.agree_data_sharing} onChange={set} testid="cashback-agree_data_sharing" error={errors.agree_data_sharing}>
                I understand that MortgagePath may share the minimum information necessary with its selected mortgage broker,
                conveyancing and survey partners to verify this cashback claim. See our{" "}
                <Link to="/privacy" className="text-[#0F766E] underline">Privacy Policy</Link>.
              </Check>
            </div>

            <div className="flex justify-end">
              <button type="submit" className="mp-btn-primary" disabled={submitting} data-testid="cashback-submit">
                {submitting ? "Submitting…" : <>Submit my claim <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* Terms */}
      <section id="terms" className="mt-14 scroll-mt-24">
        <details className="mp-card p-6 sm:p-7 group" data-testid="cashback-terms">
          <summary className="flex items-center justify-between cursor-pointer list-none marker:hidden">
            <h2 className="font-sora text-xl font-bold text-[#0B1220] tracking-tight">£100 Homebuyer Cashback — Terms and Conditions</h2>
            <ArrowRight className="w-5 h-5 text-[#0F766E] transition-transform group-open:rotate-90 shrink-0 ml-4" />
          </summary>
          <div className="mt-6 space-y-6">
            {TERMS.map((t) => (
              <div key={t.h}>
                <h3 className="font-sora text-base font-semibold text-[#0B1220]">{t.h}</h3>
                {t.p.map((para, i) => (
                  <p key={`${t.h}-${i}`} className="mt-2 text-sm text-[#475569] leading-relaxed">{para}</p>
                ))}
              </div>
            ))}
          </div>
        </details>
        <p className="mt-4 text-xs text-[#94A3B8] leading-relaxed">{CASHBACK_SMALLPRINT}</p>
      </section>

      <div className="mt-10"><ComplianceStrip /></div>
    </div>
  );
};

export default Cashback;
